import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';
import TicketCard from '../components/TicketCard';
import CreateTicketModal from '../components/CreateTicketModal';
import TicketDetailModal from '../components/TicketDetailModal';
import CalendarView from '../components/CalendarView';
import SummaryView from '../components/SummaryView';
import ListView from '../components/ListView';
import { getPref, setPref, PREF_KEYS } from '../utils/preferences';

const PRIORITY_ORDER = { highest: 0, high: 1, medium: 2, low: 3, lowest: 4 };

const sortTickets = (list, sortBy) => {
  const sorted = [...list];
  if (sortBy === 'due_date') {
    // Tickets with no due date sort to the end; earliest due date first.
    sorted.sort((a, b) => {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return a.due_date.localeCompare(b.due_date);
    });
  } else if (sortBy === 'updated_at') {
    // Most recently updated first.
    sorted.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  } else if (sortBy === 'priority') {
    sorted.sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99));
  }
  // 'default' keeps the API's own ordering (newest ticket first).
  return sorted;
};

const BoardPage = () => {
  const { boardKey } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [boards, setBoards] = useState([]);
  const [board, setBoard] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [openTicketId, setOpenTicketId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [search, setSearch] = useState('');
  const searchDebounceRef = useRef(null);
  // Remembered across reloads/logins (see utils/preferences.js) so the
  // board doesn't reset to "All my tickets" / default sort every time.
  const [sortBy, setSortBy] = useState(() => getPref(PREF_KEYS.BOARD_SORT, 'default'));
  const [relation, setRelation] = useState(() => getPref(PREF_KEYS.BOARD_RELATION, 'all')); // 'all' | 'assigned' | 'reporter' | 'report_to'
  const [showCompleted, setShowCompleted] = useState(() => getPref(PREF_KEYS.BOARD_SHOW_COMPLETED, false));
  const [viewMode, setViewMode] = useState(() => getPref(PREF_KEYS.BOARD_VIEW_MODE, 'board')); // 'board' | 'calendar' | 'summary' | 'list'
  const [counts, setCounts] = useState({}); // { [boardKey]: { assigned_open_count, unopened_count } }

  const handleRelationChange = (value) => { setRelation(value); setPref(PREF_KEYS.BOARD_RELATION, value); };
  const handleSortChange = (value) => { setSortBy(value); setPref(PREF_KEYS.BOARD_SORT, value); };
  const handleShowCompletedChange = (value) => { setShowCompleted(value); setPref(PREF_KEYS.BOARD_SHOW_COMPLETED, value); };
  const handleViewModeChange = (value) => { setViewMode(value); setPref(PREF_KEYS.BOARD_VIEW_MODE, value); };
  useEffect(() => {
    const toList = () => setViewMode('list');
    window.addEventListener('mavis-a11y-listview', toList);
    return () => window.removeEventListener('mavis-a11y-listview', toList);
  }, []);

  const loadCounts = useCallback(async () => {
    try {
      const rows = await api.getBoardCounts();
      const byKey = {};
      rows.forEach((row) => { byKey[row.board] = row; });
      setCounts(byKey);
    } catch (error) {
      // Non-critical -- the tab badges just won't show if this fails.
    }
  }, []);

  useEffect(() => {
    api.getBoards().then(setBoards).catch(() => {});
    loadCounts();
  }, [loadCounts]);

  const loadBoard = useCallback(async (q) => {
    if (!boardKey) return;
    try {
      setLoading(true);
      const [boardData, ticketData] = await Promise.all([
        api.getBoard(boardKey),
        api.getTickets(boardKey, q, { relation, showCompleted }),
      ]);
      setBoard(boardData);
      setTickets(ticketData);
    } catch (error) {
      toast.error('Could not load board.');
    } finally {
      setLoading(false);
    }
    loadCounts();
  }, [boardKey, relation, showCompleted, loadCounts]);

  // Reset search when switching boards, and do the initial load.
  useEffect(() => {
    setSearch('');
    loadBoard('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardKey]);

  // Re-fetch (without resetting the search box) when the relation filter or
  // the completed-tickets toggle changes.
  useEffect(() => {
    loadBoard(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relation, showCompleted]);

  const handleSearchChange = (value) => {
    setSearch(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => loadBoard(value), 300);
  };

  // Deep-link from a notification: /board/SD?ticket=123
  useEffect(() => {
    const ticketParam = searchParams.get('ticket');
    if (ticketParam) {
      setOpenTicketId(Number(ticketParam));
      searchParams.delete('ticket');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const ticketsByColumn = (columnId) => sortTickets(tickets.filter((t) => t.column === columnId), sortBy);

  const handleDragStart = (e, ticket) => {
    e.dataTransfer.setData('text/plain', String(ticket.id));
  };

  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    setDragOverColumn(null);
    const ticketId = Number(e.dataTransfer.getData('text/plain'));
    const ticket = tickets.find((t) => t.id === ticketId);
    if (!ticket || ticket.column === columnId) return;

    // If the target column is a "done" column and completed tickets are
    // currently hidden, drop it straight out of the local list instead of
    // just re-coloring it in place -- otherwise it'd sit there looking
    // exactly like every other card until the next full reload.
    const targetColumn = board.columns.find((c) => c.id === columnId);
    const willBeHidden = targetColumn?.is_done_column && !showCompleted;

    setTickets((prev) => (
      willBeHidden
        ? prev.filter((t) => t.id !== ticketId)
        : prev.map((t) => (t.id === ticketId ? { ...t, column: columnId, last_reply_from: null } : t))
    ));
    try {
      await api.moveTicket(ticketId, columnId);
      loadCounts();
    } catch (error) {
      toast.error('Could not move ticket.');
      loadBoard(search);
    }
  };

  const handleCreated = (ticket) => {
    setShowCreate(false);
    setTickets((prev) => [ticket, ...prev]);
    loadCounts();
  };

  const handleTicketChanged = (updated) => {
    setTickets((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
    loadCounts();
  };

  const handleTicketDeleted = (id) => {
    setTickets((prev) => prev.filter((t) => t.id !== id));
  };

  if (loading || !board) {
    return <div className="board-page"><p>Loading board...</p></div>;
  }

  return (
    <div className="board-page">
      <div className="board-header">
        <div>
          <h2>{board.name}</h2>
          <div className="board-tabs">
            {boards.map((b) => {
              const c = counts[b.key];
              return (
                <button
                  key={b.key}
                  className={`board-tab ${b.key === boardKey ? 'active' : ''}`}
                  onClick={() => navigate(`/board/${b.key}`)}
                >
                  {b.name}
                  {!!c?.assigned_open_count && (
                    <span className="board-tab-count" title="Open tickets assigned to you">
                      {c.assigned_open_count}
                    </span>
                  )}
                  {!!c?.unopened_count && (
                    <span className="board-tab-count board-tab-count-new" title="Assigned to you and not opened yet">
                      {c.unopened_count} new
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="form-input"
            style={{ width: 220 }}
            placeholder="Search tickets..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            aria-label="Search tickets"
          />
          <select
            className="form-input"
            style={{ width: 170 }}
            value={relation}
            onChange={(e) => handleRelationChange(e.target.value)}
            aria-label="Filter by your relation to the ticket"
          >
            <option value="all">All my tickets</option>
            <option value="assigned">Assigned to me</option>
            <option value="reporter">Created by me</option>
            <option value="report_to">Report to me</option>
          </select>
          <select
            className="form-input"
            style={{ width: 160 }}
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
            aria-label="Sort tickets"
          >
            <option value="default">Sort: Default</option>
            <option value="due_date">Sort: Due date</option>
            <option value="updated_at">Sort: Latest update</option>
            <option value="priority">Sort: Priority</option>
          </select>
          <label className="checkbox-row" style={{ margin: 0 }}>
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => handleShowCompletedChange(e.target.checked)}
            />
            Show completed
          </label>
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>+ New ticket</button>
        </div>
      </div>

      <div className="segmented-control">
        <button type="button" className={viewMode === 'board' ? 'active' : ''} onClick={() => handleViewModeChange('board')}>Board</button>
        <button type="button" className={viewMode === 'calendar' ? 'active' : ''} onClick={() => handleViewModeChange('calendar')}>Calendar</button>
        <button type="button" className={viewMode === 'summary' ? 'active' : ''} onClick={() => handleViewModeChange('summary')}>Summary</button>
        <button type="button" className={viewMode === 'list' ? 'active' : ''} onClick={() => handleViewModeChange('list')}>List</button>
      </div>

      {viewMode === 'board' && (
        <div className="board-columns">
          {board.columns.map((col) => {
            const colTickets = ticketsByColumn(col.id);
            return (
              <div
                key={col.id}
                className={`board-column ${dragOverColumn === col.id ? 'drag-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverColumn(col.id); }}
                onDragLeave={() => setDragOverColumn(null)}
                onDrop={(e) => handleDrop(e, col.id)}
              >
                <div className="board-column-header">
                  <span>{col.name}</span>
                  <span className="column-count">{colTickets.length}</span>
                </div>
                {colTickets.map((ticket) => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onOpen={setOpenTicketId}
                    onDragStart={handleDragStart}
                    isDoneColumn={col.is_done_column}
                  />
                ))}
                {colTickets.length === 0 && (
                  <p style={{ fontSize: '0.8rem', color: '#5b6577', textAlign: 'center', padding: '1rem 0' }}>No tickets</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {viewMode === 'calendar' && (
        <CalendarView tickets={tickets} onOpen={setOpenTicketId} />
      )}

      {viewMode === 'summary' && (
        <SummaryView tickets={tickets} columns={board.columns} />
      )}

      {viewMode === 'list' && (
        <ListView
          tickets={tickets} columns={board.columns} onOpen={setOpenTicketId}
          onTicketChanged={handleTicketChanged}
          boards={boards} currentBoardKey={boardKey}
          relation={relation} showCompleted={showCompleted} search={search}
        />
      )}

      {showCreate && (
        <CreateTicketModal board={board} onClose={() => setShowCreate(false)} onCreated={handleCreated} />
      )}

      {openTicketId && (
        <TicketDetailModal
          ticketId={openTicketId}
          board={board}
          onClose={() => setOpenTicketId(null)}
          onChanged={handleTicketChanged}
          onDeleted={handleTicketDeleted}
        />
      )}
    </div>
  );
};

export default BoardPage;
